import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Form,
  FormField,
  Heading,
  Layer,
  List,
  Menu,
  Spinner,
  Text,
  TextInput
} from 'grommet'
import { Close, Add, Trash } from 'grommet-icons'
import { Constants, I18n } from 'galarm-config'
import { useQuery } from '@tanstack/react-query'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore'
import GlobalConfig from '../GlobalConfig'
import ErrorProvider from './ErrorProvider'
import { SecondaryText } from 'web-components'
import {
  validateEmail,
  validateFirstName,
  validateLastName
} from '../utils/validations'
import { StringUtils } from 'galarm-shared'
import { getFunctions, httpsCallable } from 'firebase/functions'

const fetchAccountManagers = async ({ queryKey }) => {
  const enterpriseAccountId = queryKey[1]

  // Fetch the enterprise account
  const accountDoc = await getDoc(
    doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId)
  )

  if (!accountDoc.exists()) {
    throw new Error('Enterprise account not found')
  }

  const accountData = accountDoc.data()
  const managerIds = [
    accountData.owner,
    ...(accountData.additionalAccountManagers || [])
  ]

  // Fetch all manager details
  const managers = []
  for (const managerId of managerIds) {
    const userDoc = await getDoc(
      doc(GlobalConfig.firestoreDb, 'enterpriseUsers', managerId)
    )
    if (userDoc.exists()) {
      managers.push({
        id: managerId,
        ...userDoc.data(),
        isOwner: managerId === accountData.owner
      })
    }
  }

  return managers
}

const ManagerSummary = ({ item, index, onRemoveManager }) => {
  const menuItems = []

  if (!item.isOwner) {
    menuItems.push({
      label: I18n.t('delete'),
      onClick: () => onRemoveManager(item.id)
    })
  }

  return (
    <Box
      direction="row"
      justify="between"
      align="center"
      background={index % 2 ? 'lightTint' : 'white'}
      pad="small">
      <Box>
        <Text>{item.name || item.email}</Text>
        {item.name && <SecondaryText size="small">{item.email}</SecondaryText>}
        {item.isOwner && (
          <Text size="small" color="brand">
            {I18n.t('owner')}
          </Text>
        )}
      </Box>
      {!item.isOwner && (
        <Menu
          dropBackground="lightTint"
          alignSelf="start"
          size="small"
          icon={<Trash color="textColor" />}
          items={menuItems}
        />
      )}
    </Box>
  )
}

const AccountManagers = ({ enterpriseAccountId, onClose }) => {
  const [showAddManager, setShowAddManager] = useState(false)
  const [newManagerData, setNewManagerData] = useState({
    email: '',
    firstName: '',
    lastName: ''
  })
  const [errors, setErrors] = useState([])

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ['accountManagers', enterpriseAccountId],
    queryFn: fetchAccountManagers
  })

  const addError = error => setErrors(errors.concat([error]))
  const onClearError = errorToClear => {
    const newErrors = errors.filter(error => error !== errorToClear)
    setErrors(newErrors)
  }

  const onAddManager = async ({ value }) => {
    const { email, firstName, lastName } = value
    console.log('Adding manager with email:', email)

    GlobalConfig.showProgress({
      state: Constants.ProgressStates.IN_PROGRESS,
      message: I18n.t('addingAccountManager'),
      closeable: false
    })

    try {
      // Find the user by email
      const userSnapshot = await getDocs(
        query(
          collection(GlobalConfig.firestoreDb, 'enterpriseUsers'),
          where('email', '==', email)
        )
      )

      let userId

      if (userSnapshot.empty) {
        const displayName = StringUtils.createDisplayName(firstName, lastName)

        const functions = getFunctions()
        const createUserWithEmail = httpsCallable(
          functions,
          'createUserWithEmail'
        )
        const result = await createUserWithEmail({
          firstName,
          lastName,
          email: email,
          enterpriseAccountId,
          displayName: displayName
        })
        userId = result.data.uid
      } else {
        userId = userSnapshot.docs[0].id

        // Update the existing user with organizations array
        await updateDoc(
          doc(GlobalConfig.firestoreDb, 'enterpriseUsers', userId),
          {
            organizations: arrayUnion(enterpriseAccountId)
          }
        )
      }

      // Check if already a manager
      if (data.some(manager => manager.id === userId)) {
        addError(I18n.t('userAlreadyManager'))
        return
      }

      // Add to additionalAccountManagers
      await updateDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId
        ),
        {
          additionalAccountManagers: arrayUnion(userId)
        }
      )

      setNewManagerData({ email: '', firstName: '', lastName: '' })
      setShowAddManager(false)
      refetch()
      GlobalConfig.hideProgress()
    } catch (error) {
      console.error('Error adding manager:', error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  const onRemoveManager = async managerId => {
    try {
      await updateDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId
        ),
        {
          additionalAccountManagers: arrayRemove(managerId)
        }
      )
      refetch()
    } catch (error) {
      console.error('Error removing manager:', error)
      addError(error.message)
    }
  }

  return (
    <Layer
      onClickOutside={onClose}
      onEsc={onClose}
      position="center"
      background="lightTint"
      style={{ overflow: 'auto', maxHeight: '90vh' }}>
      <ErrorProvider
        errors={errors}
        getErrorMessage={error => error}
        onClearError={onClearError}>
        <Box pad="medium" gap="small" width="large" flex={{ shrink: 0 }}>
          <Box direction="row" justify="between">
            <Heading margin="small" level={3}>
              {I18n.t('accountManagers')}
            </Heading>
            <Button icon={<Close />} onClick={onClose} plain />
          </Box>

          {isPending && (
            <Box pad="medium" align="center" gap="small">
              <Spinner />
              <Text>{I18n.t('fetchingData')}</Text>
            </Box>
          )}

          {error && (
            <Box pad="medium" align="center" gap="small">
              <Text color="status-error">
                {I18n.t('errorFetchingData', { message: error.message })}
              </Text>
            </Box>
          )}

          {data && (
            <Box gap="small">
              <Card background="textBackgroundColor">
                <CardBody margin="none">
                  {data.length === 0 ? (
                    <Text margin="small">
                      {I18n.t('noAccountManagersConfigured')}
                    </Text>
                  ) : (
                    <List border={false} pad="none" margin="none" data={data}>
                      {(item, index) => (
                        <ManagerSummary
                          item={item}
                          index={index}
                          onRemoveManager={onRemoveManager}
                        />
                      )}
                    </List>
                  )}
                </CardBody>
              </Card>

              {!showAddManager && (
                <Button
                  primary
                  icon={<Add color="white" />}
                  label={I18n.t('accountManager')}
                  style={{ color: 'white' }}
                  onClick={() => setShowAddManager(true)}
                  alignSelf="start"
                />
              )}

              {showAddManager && (
                <Card background="white" elevation="small" pad="medium">
                  <Heading level={4} margin="none">
                    {I18n.t('addAccountManager')}
                  </Heading>
                  <Form
                    value={newManagerData}
                    onChange={nextValue => setNewManagerData(nextValue)}
                    validate="submit"
                    onSubmit={onAddManager}>
                    <FormField
                      htmlFor="email"
                      name="email"
                      label={
                        <SecondaryText weight={500}>
                          {I18n.t('email')}
                        </SecondaryText>
                      }
                      validate={validateEmail}
                      required>
                      <TextInput id="email" name="email" />
                    </FormField>
                    <Box direction="row" gap="medium">
                      <FormField
                        flex
                        htmlFor="firstName"
                        name="firstName"
                        label={
                          <SecondaryText weight={500}>
                            {I18n.t('firstName')}
                          </SecondaryText>
                        }
                        validate={validateFirstName}
                        required>
                        <TextInput id="firstName" name="firstName" />
                      </FormField>
                      <FormField
                        flex
                        htmlFor="lastName"
                        name="lastName"
                        label={
                          <SecondaryText weight={500}>
                            {I18n.t('lastName')}
                          </SecondaryText>
                        }
                        validate={validateLastName}
                        required>
                        <TextInput id="lastName" name="lastName" />
                      </FormField>
                    </Box>
                    <Box
                      direction="row"
                      gap="small"
                      justify="end"
                      margin={{ top: 'small' }}>
                      <Button
                        label={I18n.t('cancel')}
                        onClick={() => {
                          setShowAddManager(false)
                          setNewManagerData({
                            email: '',
                            firstName: '',
                            lastName: ''
                          })
                        }}
                      />
                      <Button
                        type="submit"
                        label={I18n.t('add')}
                        primary
                        style={{ color: 'white' }}
                      />
                    </Box>
                  </Form>
                </Card>
              )}
            </Box>
          )}
        </Box>
      </ErrorProvider>
    </Layer>
  )
}

export default AccountManagers
